import React, { useEffect, useMemo } from 'react';
import {
    alpha,
    Box,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    MenuItem,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGauge } from '@fortawesome/pro-solid-svg-icons';
import { Outlet, useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useApplicationContext } from 'ApplicationContext';
import {
    SETTINGS_BASE_PATH,
    SETTINGS_GROUP_ORDER,
    SettingsGroup,
    SettingsSection,
    settingsSectionPath,
    visibleSettingsSections,
} from './settingsSections';

/** Breite der Bereichsleiste. Reicht für die längsten Bezeichnungen in allen Sprachen. */
const NAV_WIDTH = 216;

/** Breite der Inhaltsspalte. Formulare über ~860px liest niemand mehr in einer Zeile. */
const CONTENT_MAX_WIDTH = 856;

/**
 * Rahmen der Verwaltung unter <c>/admin/settings</c>: links die Bereiche, rechts die
 * jeweilige Seite.
 *
 * Bewusst eine Liste und keine Tabs — Tabs brechen schon bei sechs Einträgen um, und die
 * Zahl der Bereiche wächst weiter. Auf schmalen Displays wird aus der Liste eine Auswahl,
 * damit die Seite nicht zweispaltig gequetscht wird.
 *
 * Die Leiste liegt bewusst ohne Rahmen direkt auf dem Seitengrund: die App-Sidebar ist die
 * einzige Navigation, die wie eine Fläche aussieht — diese hier ist ihr untergeordnet.
 * Die Kopfleiste trägt „Einstellungen"; den Bereichsnamen zeigt jede Seite selbst über
 * den `SettingsPageHeader`.
 */
export const AdminSettingsLayout: React.FC = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const context = useApplicationContext();
    const isNarrow = useMediaQuery(theme.breakpoints.down('md'));

    const sections = useMemo(
        () => visibleSettingsSections(context.userData!.userGroup!),
        [context.userData],
    );

    // Auf jeder Unterseite neu setzen: Seiten wie AdminTerms setzen den Titel für ihre
    // eigenständige Route (/terms) selbst und liefen sonst hier hinein.
    useEffect(() => {
        context.setTitle(t('settings.title'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname, t]);

    /** Der Bereich, dessen Pfad am besten zur aktuellen URL passt. Leer auf der Übersicht. */
    const activePath = useMemo(() => {
        const match = sections
            .map(settingsSectionPath)
            .filter((path): path is string => path !== undefined)
            .find((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
        return match ?? SETTINGS_BASE_PATH;
    }, [location.pathname, sections]);

    const grouped = useMemo(() => {
        const map = new Map<SettingsGroup, SettingsSection[]>();
        sections.forEach((section) => {
            // Bereiche ohne Detailseite erscheinen nur als Kachel in der Übersicht,
            // nicht in der Navigation — ein Eintrag, der nirgends hinführt, verwirrt nur.
            if (!section.path) return;
            const list = map.get(section.group) ?? [];
            list.push(section);
            map.set(section.group, list);
        });
        return map;
    }, [sections]);

    /** Ein Eintrag der Leiste: abgerundet, gewählt als getönte Pille in der Primärfarbe. */
    const itemSx = {
        borderRadius: 2,
        mb: 0.25,
        py: 0.75,
        color: 'text.secondary',
        '& .MuiListItemIcon-root': { minWidth: 32, color: 'text.disabled', fontSize: '0.9rem' },
        '& .MuiListItemText-primary': { fontSize: '0.875rem' },
        '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        '&.Mui-selected': {
            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.24 : 0.12),
            color: theme.palette.mode === 'dark' ? 'primary.light' : 'primary.dark',
            '& .MuiListItemText-primary': { fontWeight: 600 },
            '& .MuiListItemIcon-root': { color: 'primary.main' },
            '&:hover': {
                bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.3 : 0.18),
            },
        },
    } as const;

    const groupLabelSx = {
        display: 'block',
        px: 1.5,
        mt: 2.5,
        mb: 0.5,
        fontSize: '0.6875rem',
        fontWeight: 650,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'text.disabled',
    } as const;

    const navigation = isNarrow ? (
        <TextField
            select
            fullWidth
            size="small"
            label={t('settings.area')}
            value={activePath}
            onChange={(event) => navigate(event.target.value)}
            sx={{ mb: 2 }}
        >
            <MenuItem value={SETTINGS_BASE_PATH}>{t('settings.overview.title')}</MenuItem>
            {SETTINGS_GROUP_ORDER.flatMap((group) =>
                (grouped.get(group) ?? []).map((section) => (
                    <MenuItem key={section.key} value={settingsSectionPath(section)!}>
                        {t(section.labelKey)}
                    </MenuItem>
                )),
            )}
        </TextField>
    ) : (
        <Box
            component="nav"
            sx={{ width: NAV_WIDTH, flexShrink: 0, alignSelf: 'flex-start', position: 'sticky', top: 16 }}
        >
            <List dense disablePadding>
                <ListItemButton
                    sx={itemSx}
                    selected={activePath === SETTINGS_BASE_PATH}
                    onClick={() => navigate(SETTINGS_BASE_PATH)}
                >
                    <ListItemIcon>
                        <FontAwesomeIcon icon={faGauge} />
                    </ListItemIcon>
                    <ListItemText primary={t('settings.overview.title')} />
                </ListItemButton>
                {SETTINGS_GROUP_ORDER.map((group) => {
                    const items = grouped.get(group) ?? [];
                    if (items.length === 0) return null;
                    return (
                        <React.Fragment key={group}>
                            <Typography component="li" sx={{ ...groupLabelSx, listStyle: 'none' }}>
                                {t(`settings.groups.${group}`)}
                            </Typography>
                            {items.map((section) => {
                                const path = settingsSectionPath(section)!;
                                return (
                                    <ListItemButton
                                        key={section.key}
                                        sx={itemSx}
                                        selected={activePath === path}
                                        onClick={() => navigate(path)}
                                    >
                                        <ListItemIcon>
                                            <FontAwesomeIcon icon={section.icon} />
                                        </ListItemIcon>
                                        <ListItemText primary={t(section.labelKey)} />
                                    </ListItemButton>
                                );
                            })}
                        </React.Fragment>
                    );
                })}
            </List>
        </Box>
    );

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: isNarrow ? 'column' : 'row',
                gap: 3.5,
                alignItems: 'stretch',
            }}
        >
            {navigation}
            <Box sx={{ flexGrow: 1, minWidth: 0, maxWidth: CONTENT_MAX_WIDTH }}>
                <Outlet />
            </Box>
        </Box>
    );
};

export default AdminSettingsLayout;
